import type { RouteObject } from 'react-router-dom';
import { AppLayout } from '../shell/AppLayout';
import { RouteErrorScreen } from '../shell/RouteErrorScreen';
import { CalibrationPage } from '../calibration/CalibrationPage';
import { CameraCheckPage } from '../camera-check/CameraCheckPage';
import { CooperativeCalibrationPage } from '../calibration/CooperativeCalibrationPage';
import { CataloguePage } from '../catalogue/CataloguePage';
import { ParentPage } from '../parent/ParentPage';
import { ProfilesPage } from '../profiles/ProfilesPage';
import { ParentSetupPage } from '../setup/ParentSetupPage';
import { GameHostPage } from './game-host/GameHostPage';
import { HomePage } from './HomePage';

/**
 * Every route carries an error element, so a failure inside one screen never takes the whole
 * shell down or leaves a blank page (architecture §22).
 */
export const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouteErrorScreen />,
    children: [
      { index: true, element: <HomePage />, errorElement: <RouteErrorScreen /> },
      { path: 'catalogue', element: <CataloguePage />, errorElement: <RouteErrorScreen /> },
      { path: 'profiles', element: <ProfilesPage />, errorElement: <RouteErrorScreen /> },
      { path: 'parent', element: <ParentPage />, errorElement: <RouteErrorScreen /> },
      // Reserved in architecture §2 since Phase 0; built in Phase 3 Slice A (CAM-01).
      { path: 'camera-check', element: <CameraCheckPage />, errorElement: <RouteErrorScreen /> },
      /**
       * The same screen, reached from Parent Setup (Phase 4 Slice A′).
       *
       * MIN §8's mission flow runs `Parent Setup → Hardware Readiness → Calibration`, and a
       * cooperative readiness check *has* to run that way round: the width it checks for exists
       * because two heroes were chosen, and only the setup screen knows that. Mounted rather than
       * copied — one screen, two ways in — and the game id it carries is what lets the sitting the
       * grown-up just set up carry on from here.
       */
      {
        path: 'play/:gameId/camera-check',
        element: <CameraCheckPage />,
        errorElement: <RouteErrorScreen />,
      },
      // Hero training (CAM-02, GDS §13); reached from the readiness screen, Phase 3 Slice C.
      { path: 'calibration', element: <CalibrationPage />, errorElement: <RouteErrorScreen /> },
      /**
       * Hero training for two (CAM-02 + CAM-07, GDS §13's cooperative verification; Slice C′).
       *
       * Carries the game id for the same reason the cooperative readiness check does: the sitting
       * a grown-up set up is what makes it cooperative at all, and the family goes on to it
       * afterwards. Refuses to train outside a two-hero sitting rather than half-training one child.
       */
      {
        path: 'play/:gameId/coop-calibration',
        element: <CooperativeCalibrationPage />,
        errorElement: <RouteErrorScreen />,
      },
      {
        path: 'play/:gameId/setup',
        element: <ParentSetupPage />,
        errorElement: <RouteErrorScreen />,
      },
      { path: 'play/:gameId', element: <GameHostPage />, errorElement: <RouteErrorScreen /> },
      { path: '*', element: <RouteErrorScreen /> },
    ],
  },
];
